import { useCallback, useEffect, useRef, useState } from "react";
import { Grid } from "@material-ui/core";

import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import InputText from "components/_refactor/text/input";
import useStructureQueries from "hooks/useStructureQueries";
import MaskAndValid from "utils/maskAndValid";

const STORE_PAGE_SIZE = 100;
const SEARCH_DEBOUNCE_MS = 400;

const getSelectedDefaultValue = (selected, options, idKey) => {
  if (
    selected === null ||
    selected === undefined ||
    !options ||
    options.length === 0
  )
    return null;
  const selectedNormalized =
    typeof selected === "string" ? parseInt(selected, 10) : selected;
  return options.find((option) => option[idKey] === selectedNormalized);
};

export default function EconomicGroupStoreFields({
  onFieldsChange,
  disableAll,
  onFieldsLoad,
  requiredFields,
  clearFilters,
  defaultGroupName,
}) {
  const snackbar = useSnackbar();
  const { getEconomicGroups, getStoreUser, getStoreGroupSearch } =
    useStructureQueries();

  const [groupNameList, setGroupNameList] = useState([]);
  const [socialNameList, setSocialNameList] = useState([]);

  const [selectedGroupName, setSelectedGroupName] = useState(null);
  const [selectedSocialName, setSelectedSocialName] = useState(null);
  const [cnpj, setCnpj] = useState("");

  const [loadingFields, setLoadingFields] = useState([]);
  const [storeSearchLoading, setStoreSearchLoading] = useState(false);

  const storeSearchTimerRef = useRef(null);
  const cnpjSearchTimerRef = useRef(null);
  const fetchStoreListRef = useRef(null);
  const isInitialGroupEffectRef = useRef(true);
  const previousGroupIdRef = useRef(null);
  const onFieldsChangeRef = useRef(onFieldsChange);
  const isAutoFillRef = useRef(false);
  const selectedGroupNameRef = useRef(null);
  const userClearedGroupRef = useRef(false);
  const defaultGroupAppliedRef = useRef(false);

  const isLoading = (field) => loadingFields?.includes(field);
  const isLoadingGroupName = isLoading("groupName");
  const isLoadingSocialName = isLoading("socialName");

  const handleLoadingFields = (fields) => {
    setLoadingFields(fields);
    if (onFieldsLoad) {
      onFieldsLoad(fields);
    }
  };

  const isDisabled = (field) => disableAll || isLoading(field);
  const isRequired = (field) => requiredFields?.includes(field);

  const mapLojaGrupoToStoreOption = (loja) => {
    const razao = (loja.RazaoSocial || loja.Apelido || loja.Nome || "").trim();
    const cnpjRaw = loja.CNPJ || loja.CnpjCpfPessoa || "";
    const masked = cnpjRaw ? MaskAndValid.maskCNPJ(cnpjRaw) : "";
    return {
      Id: loja.IdPessoaLoja ?? loja.Id,
      Nome: masked ? `${masked} - ${razao}` : razao,
      Apelido: loja.Apelido || "",
      CNPJ: cnpjRaw,
      RazaoSocial: loja.RazaoSocial || loja.Nome || "",
    };
  };

  const ensureGroupInList = (group) => {
    if (!group?.Id) return;
    setGroupNameList((prev) => {
      if (prev.some((item) => item.Id === group.Id)) return prev;
      return [...prev, group];
    });
  };

  const applyAutoFillSelection = ({ group, store, cnpjValue }) => {
    isAutoFillRef.current = true;

    if (group?.Id) {
      ensureGroupInList(group);
      previousGroupIdRef.current = group.Id;
      setSelectedGroupName(group);
    }

    if (store) {
      setSelectedSocialName(store);
    }

    if (cnpjValue) {
      setCnpj(MaskAndValid.maskCNPJ(cnpjValue));
    }

    setTimeout(() => {
      isAutoFillRef.current = false;
    }, 0);
  };

  const resolveStoreFromGroupSearch = async (params) => {
    handleLoadingFields(["groupName", "socialName"]);

    try {
      const result = await getStoreGroupSearch(params);
      const data = result?.data;

      if (!data?.IdGrupoEconomico) {
        snackbar.showMessage(
          "Nenhuma loja encontrada para os filtros informados.",
          { severity: "info" },
        );
        return null;
      }

      const group = {
        Id: data.IdGrupoEconomico,
        Nome: data.NomeGrupoEconomico,
      };

      const lojas = (data.Lojas || []).map(mapLojaGrupoToStoreOption);
      setSocialNameList(lojas);

      return { group, lojas, data };
    } catch (error) {
      snackbar.showMessage(
        "Ocorreu um erro ao buscar loja e grupo econômico.",
        { severity: "error" },
      );
      return null;
    } finally {
      handleLoadingFields([]);
    }
  };

  const mapLojasComCnpjNaLabel = (list) => {
    if (!Array.isArray(list)) return [];
    return list.map((loja) => {
      const razao = (loja.Nome || loja.Apelido || "").trim();
      const masked = loja.CNPJ ? MaskAndValid.maskCNPJ(loja.CNPJ) : "";
      return {
        ...loja,
        Nome: masked ? `${masked} - ${razao}` : razao,
      };
    });
  };

  const applySingleStoreDefault = (list) => {
    if (list.length === 1) {
      setSelectedSocialName(list[0]);
      setCnpj(MaskAndValid.maskCNPJ(list[0]?.CNPJ || ""));
    }
  };

  const fetchStoreList = useCallback(
    async ({ filtro = "", group, isSearch = false } = {}) => {
      const activeGroup = group !== undefined ? group : selectedGroupName;

      if (isSearch) {
        setStoreSearchLoading(true);
      } else {
        handleLoadingFields(["socialName"]);
      }

      try {
        const params = {
          page: 0,
          pageSize: STORE_PAGE_SIZE,
        };

        if (activeGroup?.Id) {
          params.idGrupoEconomico = activeGroup.Id;
        }

        const trimmedFiltro = (filtro || "").trim();
        if (trimmedFiltro.length >= 1) {
          params.filtro = trimmedFiltro;
        }

        const result = await getStoreUser(params);
        const value = result?.data ?? { ListLojas: [], TotalDePaginas: 0 };
        const mapped = mapLojasComCnpjNaLabel(value.ListLojas ?? []);

        setSocialNameList(mapped);

        if (!isSearch && !trimmedFiltro) {
          applySingleStoreDefault(mapped);
        }
      } catch (error) {
        snackbar.showMessage(
          "Ocorreu um erro ao buscar lojas. Verifique os parâmetros informados.",
          { severity: "error" },
        );
      } finally {
        if (isSearch) {
          setStoreSearchLoading(false);
        } else {
          handleLoadingFields([]);
        }
      }
    },
    [getStoreUser, selectedGroupName, snackbar],
  );

  useEffect(() => {
    fetchStoreListRef.current = fetchStoreList;
  }, [fetchStoreList]);

  async function fetchEconomicGroupList({ autoSelectSingle = true } = {}) {
    handleLoadingFields(["groupName"]);

    try {
      const result = await getEconomicGroups({});
      const list = result?.data ?? [];
      setGroupNameList(list);
      if (autoSelectSingle && !userClearedGroupRef.current && list.length === 1) {
        setSelectedGroupName(list[0]);
      }
    } catch (error) {
      snackbar.showMessage(
        "Ocorreu um erro ao buscar grupos econômicos. Verifique os parâmetros informados.",
        { severity: "error" },
      );
    } finally {
      handleLoadingFields([]);
    }
  }

  const onGroupNameChange = (newValue) => {
    if (!newValue || newValue === "all") {
      userClearedGroupRef.current = true;
      previousGroupIdRef.current = null;
      setSelectedGroupName(null);
      setSelectedSocialName(null);
      setCnpj("");
      fetchStoreListRef.current?.({
        filtro: "",
        group: null,
        isSearch: false,
      });
      return;
    }

    userClearedGroupRef.current = false;
    setSelectedGroupName(newValue);
    setSelectedSocialName(null);
    setCnpj("");
  };

  const onGroupNameInputChange = (event, value, reason) => {
    if (reason === "clear") {
      onGroupNameChange(null);
      return;
    }

    if (reason === "reset") return;

    if (reason === "input" && !(value || "").trim() && selectedGroupNameRef.current) {
      onGroupNameChange(null);
    }
  };

  const onSocialNameChange = async (newValue) => {
    if (!newValue || newValue === "all") {
      setCnpj("");
      setSelectedSocialName(null);
      fetchStoreListRef.current?.({
        filtro: "",
        group: selectedGroupNameRef.current,
        isSearch: false,
      });
      return;
    }

    const storeCnpj = newValue?.CNPJ || "";
    setCnpj(MaskAndValid.maskCNPJ(storeCnpj));
    setSelectedSocialName(newValue);

    if (!newValue?.Id) return;

    const resolved = await resolveStoreFromGroupSearch({
      idPessoaLoja: newValue.Id,
    });

    if (!resolved) return;

    const matchedStore =
      resolved.lojas.find((store) => store.Id === newValue.Id) || newValue;

    applyAutoFillSelection({
      group: resolved.group,
      store: matchedStore,
      cnpjValue: matchedStore.CNPJ || storeCnpj,
    });
  };

  const onSocialNameInputChange = (event, value, reason) => {
    if (reason === "clear") {
      clearTimeout(storeSearchTimerRef.current);
      setSelectedSocialName(null);
      setCnpj("");
      fetchStoreListRef.current?.({
        filtro: "",
        group: selectedGroupNameRef.current,
        isSearch: false,
      });
      return;
    }

    if (reason === "reset") {
      clearTimeout(storeSearchTimerRef.current);
      return;
    }

    if (reason !== "input") return;

    clearTimeout(storeSearchTimerRef.current);
    storeSearchTimerRef.current = setTimeout(() => {
      const trimmed = (value || "").trim();
      fetchStoreListRef.current?.({
        filtro: trimmed.length >= 1 ? trimmed : "",
        group: selectedGroupNameRef.current,
        isSearch: true,
      });
    }, SEARCH_DEBOUNCE_MS);
  };

  const reloadStoresByGroup = () => {
    fetchStoreListRef.current?.({
      filtro: "",
      group: selectedGroupNameRef.current,
      isSearch: false,
    });
  };

  const onCnpjChange = (e) => {
    const masked = MaskAndValid.maskCNPJ(e.target.value);
    setCnpj(masked);

    const digits = masked.replace(/\D/g, "");
    clearTimeout(cnpjSearchTimerRef.current);

    if (digits.length === 0) {
      setSelectedSocialName(null);
      reloadStoresByGroup();
      return;
    }

    if (digits.length < 14) {
      setSelectedSocialName(null);
      return;
    }

    cnpjSearchTimerRef.current = setTimeout(async () => {
      const resolved = await resolveStoreFromGroupSearch({ cnpj: digits });
      if (!resolved) return;

      const matchedStore =
        resolved.lojas.find(
          (store) => (store.CNPJ || "").replace(/\D/g, "") === digits,
        ) || resolved.lojas[0];

      if (!matchedStore) return;

      applyAutoFillSelection({
        group: resolved.group,
        store: matchedStore,
        cnpjValue: matchedStore.CNPJ || digits,
      });
    }, SEARCH_DEBOUNCE_MS);
  };

  useEffect(() => {
    selectedGroupNameRef.current = selectedGroupName;
  }, [selectedGroupName]);

  useEffect(() => {
    onFieldsChangeRef.current = onFieldsChange;
  }, [onFieldsChange]);

  useEffect(() => {
    fetchEconomicGroupList();
    fetchStoreListRef.current?.({ filtro: "", isSearch: false });

    return () => {
      clearTimeout(storeSearchTimerRef.current);
      clearTimeout(cnpjSearchTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (isInitialGroupEffectRef.current) {
      isInitialGroupEffectRef.current = false;
      previousGroupIdRef.current = selectedGroupName?.Id ?? null;
      return;
    }

    if (isAutoFillRef.current) {
      return;
    }

    const currentGroupId = selectedGroupName?.Id ?? null;
    if (previousGroupIdRef.current === currentGroupId) {
      return;
    }

    previousGroupIdRef.current = currentGroupId;
    setSelectedSocialName(null);
    setCnpj("");
    fetchStoreListRef.current?.({
      filtro: "",
      group: selectedGroupName,
      isSearch: false,
    });
  }, [selectedGroupName?.Id]);

  useEffect(() => {
    onFieldsChangeRef.current({
      idGrupoEconomico: selectedGroupName?.Id || null,
      idPessoa: selectedSocialName?.Id || null,
      cnpj: cnpj.replace(/\D/g, "") || null,
      groupName: selectedGroupName || null,
      socialName: selectedSocialName || null,
    });
  }, [selectedGroupName, selectedSocialName, cnpj]);

  useEffect(() => {
    if (clearFilters) {
      userClearedGroupRef.current = true;
      previousGroupIdRef.current = null;
      handleLoadingFields(["groupName"]);

      setSelectedGroupName(null);
      setSelectedSocialName(null);
      setCnpj("");
      setSocialNameList([]);

      const resetFilters = async () => {
        await fetchEconomicGroupList({ autoSelectSingle: false });
        await fetchStoreListRef.current?.({
          filtro: "",
          group: null,
          isSearch: false,
        });
        handleLoadingFields([]);
      };

      resetFilters();
    }
  }, [clearFilters]);

  useEffect(() => {
    if (userClearedGroupRef.current) return;
    if (!defaultGroupName || groupNameList.length === 0) return;
    if (defaultGroupAppliedRef.current) return;

    const defaultGroup = getSelectedDefaultValue(
      defaultGroupName,
      groupNameList,
      "Id",
    );

    if (defaultGroup && defaultGroup.Id !== selectedGroupName?.Id) {
      setSelectedGroupName(defaultGroup);
      previousGroupIdRef.current = defaultGroup.Id;
      defaultGroupAppliedRef.current = true;
    }
  }, [defaultGroupName, groupNameList, selectedGroupName?.Id]);

  return (
    <>
      <Grid item xs={3} style={{ marginBottom: "0" }}>
        <AutoCompleteField
          label="Nome do Grupo"
          onChange={onGroupNameChange}
          onInputChange={onGroupNameInputChange}
          options={groupNameList}
          disabled={isDisabled("groupName")}
          loading={isLoadingGroupName}
          value={selectedGroupName}
          optionIdKey="Id"
          required={isRequired("groupName")}
        />
      </Grid>

      <Grid item xs={6} style={{ marginBottom: "0" }}>
        <AutoCompleteField
          label="Razão Social / Loja"
          onChange={onSocialNameChange}
          options={socialNameList}
          disabled={isDisabled("socialName") || socialNameList.length === 1}
          loading={isLoadingSocialName}
          optionsSearchLoading={storeSearchLoading}
          onInputChange={onSocialNameInputChange}
          preserveInputWhenLoading
          disableClientFilter
          value={
            selectedSocialName ??
            (socialNameList?.length === 1 ? socialNameList[0] : null)
          }
          optionIdKey="Id"
          required={isRequired("socialName")}
          ignoreCnpjPunctuationInSearch
        />
      </Grid>

      <Grid item xs={3}>
        <InputText
          label="CNPJ"
          placeholder="00.000.000/0000-00"
          value={cnpj}
          onChange={onCnpjChange}
          disabled={
            isDisabled("cnpj") || isLoadingSocialName || isLoadingGroupName
          }
          required={isRequired("cnpj")}
        />
      </Grid>
    </>
  );
}
